import { z } from 'zod';

export const FicheSectionType = z.enum([
  'intro',
  'classification',
  'drug_card',
  'comparison_table',
  'clinical_case',
  'warning',
  'mnemonic',
  'diagram',
  'free_text',
]);
export type FicheSectionType = z.infer<typeof FicheSectionType>;

export const FicheColor = z.enum(['blue', 'green', 'purple', 'orange', 'red', 'yellow', 'neutral']);
export type FicheColor = z.infer<typeof FicheColor>;

export const TemplateId = z.enum([
  'dessinee',
  'clinique',
  'infographique',
  'comparative',
  'flowchart',
  'minimaliste',
]);
export type TemplateId = z.infer<typeof TemplateId>;

export const Language = z.enum(['fr', 'en']);
export type Language = z.infer<typeof Language>;

export const Bullet = z.object({
  id: z.string(),
  text: z.string(),
  emphasis: FicheColor.optional(),
});
export type Bullet = z.infer<typeof Bullet>;

export const MiniHeader = z.object({
  id: z.string(),
  icon: z.string().optional(),
  label: z.string(),
  color: FicheColor.optional(),
  bullets: z.array(Bullet),
});
export type MiniHeader = z.infer<typeof MiniHeader>;

export const DrugCard = z.object({
  id: z.string(),
  name: z.string(),
  color: FicheColor,
  icon: z.string().optional(),
  intro_bullets: z.array(Bullet),
  sub_sections: z.array(MiniHeader),
});
export type DrugCard = z.infer<typeof DrugCard>;

export const FicheSection = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('intro'),
    id: z.string(),
    title: z.string(),
    icon: z.string().optional(),
    color: FicheColor,
    bullets: z.array(Bullet),
    warning: z.string().optional(),
  }),
  z.object({
    type: z.literal('classification'),
    id: z.string(),
    title: z.string(),
    icon: z.string().optional(),
    color: FicheColor,
    bullets: z.array(Bullet),
    note: z.string().optional(),
  }),
  z.object({
    type: z.literal('drug_card'),
    id: z.string(),
    drug: DrugCard,
  }),
  z.object({
    type: z.literal('comparison_table'),
    id: z.string(),
    title: z.string(),
    columns: z.array(z.string()),
    rows: z.array(
      z.object({
        label: z.string(),
        cells: z.array(z.string()),
      }),
    ),
  }),
  z.object({
    type: z.literal('clinical_case'),
    id: z.string(),
    patient: z.string(),
    prescription: z.array(z.string()),
    question: z.string(),
    conduite: z.array(Bullet),
  }),
  z.object({
    type: z.literal('warning'),
    id: z.string(),
    text: z.string(),
    icon: z.string().optional(),
  }),
  z.object({
    type: z.literal('mnemonic'),
    id: z.string(),
    text: z.string(),
    explanation: z.string().optional(),
  }),
  z.object({
    type: z.literal('diagram'),
    id: z.string(),
    diagram_id: z.string(),
  }),
  z.object({
    type: z.literal('free_text'),
    id: z.string(),
    title: z.string().optional(),
    body: z.string(),
  }),
]);
export type FicheSection = z.infer<typeof FicheSection>;

export const DiagramNodeKind = z.enum([
  'receptor',
  'molecule',
  'process',
  'outcome',
  'drug',
  'label',
]);
export type DiagramNodeKind = z.infer<typeof DiagramNodeKind>;

export const DiagramEdgeKind = z.enum(['flow', 'inhibition', 'activation']);
export type DiagramEdgeKind = z.infer<typeof DiagramEdgeKind>;

export const DiagramNode = z.object({
  id: z.string(),
  type: DiagramNodeKind,
  label: z.string(),
  sublabel: z.string().optional(),
  color: FicheColor.optional(),
  position: z.object({ x: z.number(), y: z.number() }),
});
export type DiagramNode = z.infer<typeof DiagramNode>;

export const DiagramEdge = z.object({
  id: z.string(),
  source: z.string(),
  target: z.string(),
  label: z.string().optional(),
  kind: DiagramEdgeKind,
});
export type DiagramEdge = z.infer<typeof DiagramEdge>;

export const Diagram = z.object({
  id: z.string(),
  title: z.string().optional(),
  nodes: z.array(DiagramNode),
  edges: z.array(DiagramEdge),
});
export type Diagram = z.infer<typeof Diagram>;

export const FicheSourceKind = z.enum(['pdf', 'image', 'text']);
export type FicheSourceKind = z.infer<typeof FicheSourceKind>;

export const FicheSource = z.object({
  kind: FicheSourceKind,
  label: z.string(),
  extracted_chars: z.number(),
  page_range: z.tuple([z.number(), z.number()]).optional(),
});
export type FicheSource = z.infer<typeof FicheSource>;

export const FicheMeta = z.object({
  title: z.string(),
  subtitle: z.string().optional(),
  tags: z.array(z.string()).default([]),
  template_id: TemplateId,
  language: Language,
  created_at: z.string(),
  updated_at: z.string(),
});
export type FicheMeta = z.infer<typeof FicheMeta>;

export const Fiche = z.object({
  schema_version: z.literal(1),
  id: z.string(),
  meta: FicheMeta,
  sections: z.array(FicheSection),
  diagrams: z.array(Diagram).default([]),
  sources: z.array(FicheSource).default([]),
});
export type Fiche = z.infer<typeof Fiche>;

export const SCHEMA_VERSION = 1 as const;
